import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { share, map, catchError } from 'rxjs/operators';

import { MainConfig } from '../../conf/main.config';
import {
    LoginUser,
    RegisterUser,
    RegistrationPolicy,
    UsernameAvailability,
    LoginPolicy,
    JWT,
    TokenRefresh,
    LoginPayload,
    LogoutPayload,
} from './models';

@Injectable()
export class AuthService {
    private headers: HttpHeaders = new HttpHeaders({
        'Content-Type': 'application/json',
    });

    redirectUrl: string;
    authenticated$ = new BehaviorSubject<boolean>(this.hasRefreshToken());
    authorized$ = new BehaviorSubject<boolean>(
        this.hasAccessToken() && this.tokenExpiration(this.getAccessToken()) > 1
    );
    timeoutID: any = null;

    constructor(private http: HttpClient, private router: Router) { }

    login(
        user: LoginUser,
        rememberSession: boolean = false
    ): Observable<LoginPayload> {
        const url = `${MainConfig.API_ENDPOINT}/login`;

        // Enviamos ao backend somente as credenciais necessárias.
        // A opção "manter sessão ativa" altera a validade do refresh token no
        // servidor. A senha nunca é gravada em storage, URL ou estado persistente.
        const credentials = {
            email: user.email,
            password: user.password,
            remember_session: rememberSession,
        };

        return this.http
            .post<LoginPayload>(url, credentials, { headers: this.headers })
            .pipe(
                map((response) => {
                    if (response) {
                        this.authenticate(response, rememberSession);
                    }
                    return response;
                })
            );
    }

    register(user: RegisterUser): Observable<any> {
        let url: string = `${MainConfig.API_ENDPOINT}/registration`;
        return this.http.post(url, user).pipe(
            map((user) => {
                if (user) {
                    // Mantém o comportamento legado: cadastro não autentica
                    // automaticamente. Login continua sendo uma ação explícita.
                }
                return user;
            })
        );
    }

    getRegistrationPolicy(): Observable<RegistrationPolicy> {
        const url = `${MainConfig.API_ENDPOINT}/registration/policy`;
        return this.http.get<RegistrationPolicy>(url, { headers: this.headers });
    }

    checkUsernameAvailability(username: string): Observable<UsernameAvailability> {
        const url = `${MainConfig.API_ENDPOINT}/registration/username-available`;
        return this.http.get<UsernameAvailability>(url, {
            headers: this.headers,
            params: { username: username },
        });
    }

    getLoginPolicy(): Observable<LoginPolicy> {
        const url = `${MainConfig.API_ENDPOINT}/login/policy`;
        return this.http.get<LoginPolicy>(url, { headers: this.headers });
    }

    authenticate(user: any, _rememberSession: boolean = false): void {
        // A aplicação legada lê tokens e dados públicos do usuário diretamente
        // do localStorage em vários pontos. Mantemos essa compatibilidade aqui;
        // a duração da sessão é decidida pelo backend ao emitir o refresh token.
        this.clearAuthData(false);

        localStorage.setItem('access_token', user.access_token || '');
        localStorage.setItem('refresh_token', user.refresh_token || '');
        localStorage.setItem('username', user.username || '');
        localStorage.setItem('userAvatar', user.userAvatar || '');

        // Não duplicamos a resposta inteira no storage. Ela contém e-mail e
        // tokens que já têm armazenamento próprio e não precisam de outra cópia.
        this.authorized$.next(!!user.access_token);
        this.authenticated$.next(!!user.refresh_token);
    }

    logout(): Promise<any> {
        const url: string = `${MainConfig.API_ENDPOINT}/logout`;
        this.authorized$.next(false);

        return this.http
            .post<LogoutPayload>(url, { headers: this.headers })
            .pipe(
                map((response) => {
                    this.clearAuthData();
                    return response;
                }),
                catchError((error) => {
                    // Mesmo com falha de rede, removemos apenas dados de autenticação.
                    // Idioma e outras preferências do usuário continuam preservados.
                    console.error(`[logout] error "${error}"`);
                    this.clearAuthData();
                    this.router.navigateByUrl('/home');
                    return [null];
                })
            )
            .toPromise();
    }

    ensureAuthorized(): Observable<LoginUser> {
        let url: string = `${MainConfig.API_ENDPOINT}/user/info`;
        return this.http.get<LoginUser>(url, { headers: this.headers });
    }

    performTokenRefresh(): Observable<TokenRefresh> {
        const url: string = `${MainConfig.API_ENDPOINT}/token_refresh`;
        const refresh_token = this.getRefreshToken();
        const headers = this.headers.set(
            'Authorization',
            `Bearer ${refresh_token}`
        );
        return this.http.post<TokenRefresh>(url, null, {
            headers: headers,
        });
    }

    selfDeleteAccount(_access_token): Promise<any> {
        let url: string = `${MainConfig.API_ENDPOINT}/user/delete`;
        return this.http.delete(url, { headers: this.headers }).toPromise();
    }

    isLoggedIn(): Observable<boolean> {
        return this.authorized$.pipe(share());
    }

    getRefreshToken(): string {
        return localStorage.getItem('refresh_token');
    }

    getAccessToken(): string {
        return localStorage.getItem('access_token');
    }

    private hasRefreshToken(): boolean {
        return !!localStorage.getItem('refresh_token');
    }

    private hasAccessToken(): boolean {
        return !!localStorage.getItem('access_token');
    }

    getUserInfo(): object {
        const username = localStorage.getItem('username');
        const userAvatar = localStorage.getItem('userAvatar');

        return username
            ? {
                  username: username,
                  userAvatar: userAvatar,
              }
            : null;
    }

    clearAuthData(updateSubjects: boolean = true): void {
        // Remove somente dados ligados à autenticação. Preferências como idioma
        // continuam intactas depois de sair da conta.
        [
            'access_token',
            'refresh_token',
            'username',
            'userAvatar',
            'userInfo',
        ].forEach((key) => localStorage.removeItem(key));

        if (updateSubjects) {
            this.authorized$.next(false);
            this.authenticated$.next(false);
        }
    }

    decodeToken(token: string): JWT {
        if (!token) return;
        const parts: any[] = token.split('.');
        if (parts.length != 3) return;
        try {
            return {
                header: JSON.parse(atob(parts[0])),
                payload: JSON.parse(atob(parts[1])),
            };
        } catch (error) {
            console.error(error);
            return;
        }
    }

    tokenExpiration(token: string): number {
        if (!token) return;
        const jwt = this.decodeToken(token);
        if (!jwt) return;
        const now: number = new Date().getTime();
        const delta: number = (jwt.payload.exp * 1000 - now) / 1000.0;
        return delta;
    }

    confirmEmail(token): Observable<any> {
        let url: string = `${MainConfig.API_ENDPOINT}/user/confirmEmail/${token}`;
        return this.http.get(url);
    }
}
